// Plain-words text for the product page's 3D model panel (USA-1624).

export type Size = { width: number; depth: number; height: number }

function inches(value: number) {
  return String(Math.round(value * 10) / 10)
}

/** "72 × 24 × 35 in" (width × depth × height). */
export function formatSize(size: Size) {
  return `${inches(size.width)} × ${inches(size.depth)} × ${inches(size.height)} in`
}

/** AutoQuotes' size, when it lists all three sides. */
export function catalogSize(
  width?: string | number | null,
  depth?: string | number | null,
  height?: string | number | null,
) {
  const [w, d, h] = [width, depth, height].map((value) => Number(value ?? 0))
  return w && d && h && w > 0 && d > 0 && h > 0 ? { width: w, depth: d, height: h } : null
}

const SIZES = /model ([\d.]+)x([\d.]+)x([\d.]+) in, AutoQuotes ([\d.]+)x([\d.]+)x([\d.]+) in/

/** Why a product has no model, in plain words, with the two sizes when they disagree. */
export function modelProblem(reason: string, detail: string, problems?: string | null) {
  const sizes = SIZES.exec(detail)
  switch (reason) {
    case 'size_mismatch':
      return {
        text: "The 3D file's size doesn't match AutoQuotes.",
        sizes: sizes
          ? {
              model: formatSize({
                width: +sizes[1]!,
                depth: +sizes[2]!,
                height: +sizes[3]!,
              }),
              catalog: formatSize({
                width: +sizes[4]!,
                depth: +sizes[5]!,
                height: +sizes[6]!,
              }),
            }
          : null,
      }
    case 'review_rejected':
      return {
        text: problems?.trim()
          ? `The AI review rejected the model: ${problems.trim()}`
          : 'The AI review rejected the model.',
        sizes: null,
      }
    case 'unreadable':
      return { text: "The 3D file can't be read.", sizes: null }
    case 'no_mesh':
    case 'solid_only':
      return {
        text: 'The 3D file has no shapes that can be drawn.',
        sizes: null,
      }
    case 'too_simple':
      return {
        text: 'The 3D file is only a few flat shapes, too simple to be the product.',
        sizes: null,
      }
    case 'sparse':
      return {
        text: 'The 3D file draws only a few scattered parts, such as the feet.',
        sizes: null,
      }
    case 'incomplete':
      return {
        text: "Parts of the 3D file can't be read, so the model would be missing pieces.",
        sizes: null,
      }
    case 'download':
      return {
        text: "The 3D file couldn't be downloaded. Try again later.",
        sizes: null,
      }
    default:
      return { text: "The 3D file can't be turned into a model.", sizes: null }
  }
}
